import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { DateTime, Info } from 'luxon'
import { useWidget } from '../../../providers/widget'
import { CalendarWidgetConfig } from '../config'
import { useFetchAvailability, useFetchProperty } from '../data'
import { CalendarUtils } from '../calendar-utils'
import {
  StaySuggestion,
  formatSuggestionDay,
  resolveCheckinSuggestion,
  resolveCheckoutSuggestion,
} from '../stay-suggestion'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import { toBcp47, isEnglish, resolveLocaleTag } from '../../../common/locale-format'
import CalendarMonth, { type CalendarMonthProps } from './CalendarMonth'
import CalendarSkeleton, { type CalendarSkeletonProps } from './CalendarSkeleton'
import WidgetDataError from '../../../components/WidgetDataError'

const WEEKDAY_LABELS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// #1531: leaving a day cell hides its tip after this grace so the pointer can
// travel into the tip and press its action button.
const HIDE_GRACE_MS = 350

/**
 * Rounding tolerance for the cover test — deliberately far below the 10px gap
 * the tip leaves above its anchor. Anything >= 10 reaches into the anchor's OWN
 * row, so sliding sideways to the next day would hold the stale tip instead of
 * showing that day's own. The cells the pointer actually crosses on the way up
 * are ~67px tall against a 10px gap, so they already overlap the tip outright
 * and need no slop to qualify.
 */
const TIP_COVER_SLOP_PX = 2

/**
 * #1531: is `cell` underneath the open tip (so the pointer is on its way to the
 * action button, not hovering a new day)? Measured against the tip's RENDERED
 * rect, because that is the region the pointer actually has to cross: the tip is
 * `pointer-events: none` apart from its button, so every cell it covers keeps
 * receiving hover while the pointer travels over it.
 *
 * The first cut of this tested a half-plane — "any cell above a top-placed
 * anchor" — which swallowed hovers nowhere near the tip: a 260px tip spans ~4 of
 * 7 columns, so cells in the far columns, several rows up, and every cell of the
 * adjacent month's pane all qualified (verified in Chrome: hovering 2026-10-07
 * held a tip anchored on 2026-09-22).
 *
 * Layout-less environments (rects all zero) intersect trivially, so unit tests
 * still exercise the travel path.
 */
function isUnderTip(tipEl: HTMLElement | null, cell: HTMLElement): boolean {
  if (!tipEl) return false
  const t = tipEl.getBoundingClientRect()
  const c = cell.getBoundingClientRect()
  return c.right >= t.left - TIP_COVER_SLOP_PX
    && c.left <= t.right + TIP_COVER_SLOP_PX
    && c.bottom >= t.top - TIP_COVER_SLOP_PX
    && c.top <= t.bottom + TIP_COVER_SLOP_PX
}

/**
 * Does the open tip currently hold focus? `getRootNode().activeElement` is the
 * shadow-correct read: inside a shadow tree `document.activeElement` is only
 * ever the HOST, so asking the document would always say no for a mounted
 * widget. (`:focus-within` via `matches()` is not dependable across the DOM
 * implementations this runs on, including the test environment.)
 */
function tipHasFocus(tipEl: HTMLElement | null): boolean {
  if (!tipEl) return false
  const root = tipEl.getRootNode?.() as (Document | ShadowRoot | null)
  const active = root && 'activeElement' in root ? root.activeElement : null
  return !!active && tipEl.contains(active)
}

// #1530: month headers and weekday abbreviations follow the widget's resolved
// locale (`useLocaleDetection`: the configured `language.locale`, else the
// browser's) instead of Luxon's process default and a fixed English list. An
// en_* locale (or none) keeps the pre-#1530 output byte-identical, and an
// unusable tag falls back to it rather than throwing inside render.
// #1541: `toBcp47`/`isEnglish` used to be private copies here; they now come
// from the shared `common/locale-format` module (introduced in #1538 to
// mirror these exactly) so the calendar and booking widgets share one
// locale-resolution implementation. No behavior change.

export function getMonthLabel(year: number, month: number, locale?: string): string {
  const dt = DateTime.fromObject({ year, month, day: 1 })
  const tag = isEnglish(locale) ? null : toBcp47(locale)
  if (!tag) return dt.toFormat('MMMM yyyy')
  try {
    // Standalone month name (LLLL): "septembre 2026" in fr, "september 2026"
    // in nl — capitalised because it is a heading.
    const label = dt.setLocale(tag).toFormat('LLLL yyyy')
    return label.charAt(0).toLocaleUpperCase(tag) + label.slice(1)
  } catch {
    return dt.toFormat('MMMM yyyy')
  }
}

/** Sunday-first short weekday names for the locale; the English list for en_* or an unusable locale. */
export function getWeekdayLabels(locale?: string): string[] {
  const tag = isEnglish(locale) ? null : toBcp47(locale)
  if (!tag) return WEEKDAY_LABELS_EN
  try {
    const monFirst = Info.weekdays('short', { locale: tag })
    if (monFirst.length !== 7) return WEEKDAY_LABELS_EN
    return [monFirst[6], ...monFirst.slice(0, 6)]
  } catch {
    return WEEKDAY_LABELS_EN
  }
}

/**
 * #1262: the `{{value}}` the minimum-stay error substitutes — the night count
 * plus a locale-aware unit word. en_US keeps the singular for any count
 * ("a 3 night minimum", byte-identical to the former hard-coded text); a
 * shipped locale table can pluralize ("3 noches").
 */
export function minStayValueLabel(
  _t: (key: 'min_stay_unit_one' | 'min_stay_unit_other', defaultValue?: string) => string,
  nights: number,
): string {
  return `${nights} ${_t(nights === 1 ? 'min_stay_unit_one' : 'min_stay_unit_other')}`
}

/**
 * What the footer renderer receives when a widget injects its own (#920).
 * `_t` is a method signature on purpose: the hook's `_t` is typed over the
 * widget's own translation keys, and a method parameter is bivariant, so the
 * narrower function is assignable here.
 */
export interface CalendarGridFooterProps {
  onClear: () => void
  canClear: boolean
  _t(key: string, defaultValue?: string): string
  /** Today's day-of-month in the property's timezone — a live sample number for a legend. */
  sampleDay: number
}

/**
 * #920: the typographic calendar (`widgets/calendar-v2`) renders its own day
 * cells, loading skeleton and footer but shares this grid's selection,
 * hover-preview, tooltip, drag and keyboard machinery. Every prop is optional
 * and defaults to this widget's own component, so a bare `<CalendarGrid />`
 * renders exactly what it rendered before these props existed.
 */
export interface CalendarGridProps {
  MonthComponent?: React.ComponentType<CalendarMonthProps>
  SkeletonComponent?: React.ComponentType<CalendarSkeletonProps>
  /** Replaces the whole `.calendar-footer-info` block (taxes note, clear button, legend). */
  FooterComponent?: React.ComponentType<CalendarGridFooterProps>
}

export default function CalendarGrid({
  MonthComponent = CalendarMonth,
  SkeletonComponent = CalendarSkeleton,
  FooterComponent,
}: CalendarGridProps = {}) {
  const { options, widgetId } = useWidget<CalendarWidgetConfig>()

  const { data: availability, isLoading: isLoadingAvailability, isError: isErrorAvailability, error: errorAvailability } = useFetchAvailability(options.filter)
  const { data: propertyData, isLoading: isLoadingProperty, isError: isErrorProperty, error: errorProperty } = useFetchProperty(options.filter)

  const property = propertyData?.result
  const timezone = property?.timezone || 'UTC'

  // Calendar navigation state
  const [monthOffset, setMonthOffset] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Selection state (only used when allowSelectDates = true)
  const [selectedCheckin, setSelectedCheckin] = useState<string | null>(null)
  const [selectedCheckout, setSelectedCheckout] = useState<string | null>(null)
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  // Tooltip state
  const [tooltip, setTooltip] = useState<{ message: string; top: number; left: number; placement: 'top' | 'bottom'; suggestion: StaySuggestion | null; dateStr: string } | null>(null)
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Grace timer: leaving a day cell hides the tip after a short delay so the
  // pointer can travel into the tip and press its action button (toolkit
  // calendar-hover-tips HIDE_GRACE_MS).
  const tooltipHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // #1531: the tip renders over the cells around its anchor, so the pointer
  // crosses them on its way to the tip's action button. A hover on a cell the
  // tip COVERS holds the tip exactly as it is rather than replacing it — see
  // `isUnderTip` and the guard in `handleDayHover`.
  const tooltipRef = useRef<typeof tooltip>(null)
  tooltipRef.current = tooltip
  const tooltipElRef = useRef<HTMLDivElement | null>(null)
  const cancelTooltipHide = useCallback(() => {
    if (tooltipHideTimerRef.current) clearTimeout(tooltipHideTimerRef.current)
    tooltipHideTimerRef.current = null
  }, [])
  // After applying a suggestion, the cell now under the pointer must not
  // immediately pop a fresh hover tip (toolkit QUIET_AFTER_APPLY_MS).
  const hoverQuietUntilRef = useRef(0)

  // #1548 keyboard access: one roving tabstop for the whole grid, and the id the
  // focused day points at with aria-describedby when its tip is open.
  const tipDomId = `hr-cal-tip-${widgetId}`
  const [focusedDate, setFocusedDate] = useState<string | null>(null)
  // Set when something deliberately moved the tabstop, so the effect below
  // knows to pull DOM focus along; a plain re-render must not steal focus.
  const pendingFocusRef = useRef<string | null>(null)

  // Pending tooltip: queued from external events, shown after slide transition
  // `checkin` is the tip's explicit mode: a date means "this day was rejected
  // as a check-OUT for that check-in", null means "rejected as a check-IN".
  // Carried with the queued tip instead of inferred from ambient selection
  // state at fire time, which mislabelled an external invalid check-in as a
  // checkout error while a half-range was selected (codex fresh pass).
  const [pendingTooltip, setPendingTooltip] = useState<{ date: string; checkin: string | null } | null>(null)

  // Translations
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('calendar', options.translations, detectedLocale, options.language)

  // Clear tooltip timers on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
      if (tooltipHideTimerRef.current) clearTimeout(tooltipHideTimerRef.current)
    }
  }, [])

  // How far forward the calendar renders/selects: the smaller of maxCalendarDays and
  // the booking window (WP: min(max_calendar_days, booking_window)). Defaults to 365.
  const maxNumDays = useMemo(() => {
    const cap = options.maxCalendarDays || 365
    return options.bookingWindow ? Math.min(cap, options.bookingWindow) : cap
  }, [options.maxCalendarDays, options.bookingWindow])

  const calendarUtils = useMemo(() => {
    const utils = new CalendarUtils({
      timezone,
      minStay: property?.minimum_stay || 1,
      maxStay: options.maxStay || property?.maximum_stay || 365,
      maxNumDays,
      // Advance-notice window (#1071): synced from the PMS via Central on the
      // property payload; the calendar greys out dates inside it and explains
      // the rule instead of letting the pick fail later at checkout.
      advanceNoticeHours: property?.advance_notice_hours,
    })
    if (availability) {
      utils.setPriceData(availability)
    }
    return utils
  }, [availability, timezone, property?.minimum_stay, property?.maximum_stay, property?.advance_notice_hours, options.maxStay, maxNumDays])

  // Determine start month
  const startMonth = useMemo(() => {
    if (options.startMonth?.year && options.startMonth?.month) {
      return DateTime.fromObject({
        year: options.startMonth.year,
        month: options.startMonth.month,
        day: 1,
      }, { zone: timezone })
    }
    return DateTime.now().setZone(timezone).startOf('month')
  }, [options.startMonth, timezone])

  // Generate months from startMonth to cover the booking window (maxNumDays).
  // WP plugin: monthDiffCeiled + 1, where diff = endMonth - startMonth in months.
  // With default 365 days this produces 13 months.
  const allMonths = useMemo(() => {
    const endMonth = startMonth.plus({ days: maxNumDays }).startOf('month')
    const monthCount = Math.ceil(endMonth.diff(startMonth, 'months').months) + 1
    const months: Array<{ year: number; month: number }> = []
    let current = startMonth
    for (let i = 0; i < monthCount; i++) {
      months.push({ year: current.year, month: current.month })
      current = current.plus({ months: 1 })
    }
    return months
  }, [startMonth, maxNumDays])

  // Reorder the weekday header to start on the configured first day of week (WP: start_of_week).
  const firstDayOfWeek = options.firstDayOfWeek ?? 0
  const weekdayLabels = useMemo(() => {
    const base = getWeekdayLabels(detectedLocale)
    return [...base.slice(firstDayOfWeek), ...base.slice(0, firstDayOfWeek)]
  }, [firstDayOfWeek, detectedLocale])

  // #1541: calendar prices follow the same resolved locale as the month
  // header and weekday row above. `resolveLocaleTag` returns `null` for
  // English/unset/unusable locales, coerced to `undefined` here so
  // CalendarMonth's `Intl.NumberFormat` call stays byte-identical to its
  // pre-#1541 no-locale invocation in that case.
  const priceLocale = useMemo(() => resolveLocaleTag(detectedLocale) ?? undefined, [detectedLocale])

  // Max offset: with two months per view, block next when last pair is visible
  const monthsPerView = options.monthsPerView ?? 'auto'
  const maxMonthOffset = useMemo(() => {
    return Math.max(0, allMonths.length - (monthsPerView === 'single' ? 1 : 2))
  }, [allMonths, monthsPerView])

  // Discount text from property price factors (like WP plugin's homelocal_get_property_discount_text)
  const discountText = useMemo(() => {
    if (!options.showHeaderOffer || !property) return null
    const weeklyFactor = property.weekly_price_factor
    const monthlyFactor = property.monthly_price_factor
    const weekly = weeklyFactor && weeklyFactor > 0 ? Math.round((1 - weeklyFactor) * 100) : 0
    const monthly = monthlyFactor && monthlyFactor > 0 ? Math.round((1 - monthlyFactor) * 100) : 0
    if (weekly > 0 && monthly > 0) {
      return _t('header_offer_weekly_and_monthly').replace('{{weekly}}', String(weekly)).replace('{{monthly}}', String(monthly))
    }
    if (weekly > 0) {
      return _t('header_offer_weekly').replace('{{weekly}}', String(weekly))
    }
    if (monthly > 0) {
      return _t('header_offer_monthly').replace('{{monthly}}', String(monthly))
    }
    return null
  }, [options.showHeaderOffer, property, _t])

  // Local time in property timezone, updated every 60s
  const [localTime, setLocalTime] = useState<string | null>(null)
  useEffect(() => {
    if (!options.showLocalTime || !timezone) return
    const update = () => {
      setLocalTime(DateTime.now().setZone(timezone).toFormat('dd LLL, hh:mm a'))
    }
    update()
    const interval = setInterval(update, 60000)
    return () => clearInterval(interval)
  }, [options.showLocalTime, timezone])

  // Event-driven date sync: outgoing — notify external systems when dates change.
  // Skip the initial (mount) emit so a freshly mounted calendar's empty state
  // doesn't clear another widget's pre-filled dates (e.g. a booking widget seeded
  // from URL params). Only real user changes — select AND clear — broadcast.
  const didEmitRef = useRef(false)
  useEffect(() => {
    if (!didEmitRef.current) {
      didEmitRef.current = true
      return
    }
    window.dispatchEvent(new CustomEvent(`calendar-dates-changed-${widgetId}`, {
      detail: { checkin: selectedCheckin, checkout: selectedCheckout },
    }))
  }, [selectedCheckin, selectedCheckout, widgetId])

  // Event-driven date sync: incoming — listen for external date changes
  useEffect(() => {
    const eventName = `calendar-set-dates-${widgetId}`
    const handler = (e: Event) => {
      const { checkin, checkout } = (e as CustomEvent).detail || {}

      // Every incoming request supersedes a tip still queued from the last
      // one — a stale tip could otherwise fire (and offer a stay) against
      // state that has since moved on (codex closure). A pending hover-hide
      // goes with it for the same reason.
      setPendingTooltip(null)
      cancelTooltipHide()

      // Clear request
      if (!checkin && !checkout) {
        setSelectedCheckin(null)
        setSelectedCheckout(null)
        setHoveredDate(null)
        setTooltip(null)
        return
      }

      // Navigate to the check-in month regardless of validation
      if (checkin) {
        const dt = DateTime.fromISO(checkin)
        const idx = allMonths.findIndex(m => m.year === dt.year && m.month === dt.month)
        if (idx >= 0) {
          setMonthOffset(Math.min(idx, maxMonthOffset))
        }
      }

      // Validate check-in
      if (checkin) {
        const ciResult = calendarUtils.canCheckin(checkin)
        if (!ciResult.status) {
          // Navigate and show tooltip on the invalid date
          setPendingTooltip({ date: checkin, checkin: null })
          return
        }

        setSelectedCheckin(checkin)
        setHoveredDate(null)
        setTooltip(null)

        // Validate check-out against check-in
        if (checkout) {
          const coResult = calendarUtils.canCheckinout(checkin, checkout)
          if (coResult.status) {
            setSelectedCheckout(checkout)
          } else {
            setSelectedCheckout(null)
            // Show tooltip on the invalid checkout date
            setPendingTooltip({ date: checkout, checkin })
          }
        } else {
          setSelectedCheckout(null)
        }
      }
    }
    window.addEventListener(eventName, handler)
    return () => window.removeEventListener(eventName, handler)
  }, [widgetId, allMonths, maxMonthOffset, calendarUtils, cancelTooltipHide])

  const handlePrev = useCallback(() => {
    if (monthOffset <= 0) return
    setMonthOffset(prev => prev - 1)
  }, [monthOffset])

  const handleNext = useCallback(() => {
    if (monthOffset >= maxMonthOffset) return
    setMonthOffset(prev => prev + 1)
  }, [monthOffset, maxMonthOffset])

  // Drag/swipe month navigation (touch + mouse, like WP plugin's tns mouseDrag)
  const didDragRef = useRef(false)
  const sliderTrackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    startX: number
    startY: number
    currentX: number
    isDragging: boolean
    directionLocked: boolean
    containerWidth: number
  } | null>(null)

  const dragStart = useCallback((clientX: number, clientY: number) => {
    const container = sliderTrackRef.current?.parentElement
    if (!container) return
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      currentX: clientX,
      isDragging: false,
      directionLocked: false,
      containerWidth: container.offsetWidth,
    }
  }, [])

  const dragMove = useCallback((clientX: number, clientY: number) => {
    const state = dragRef.current
    if (!state) return
    const dx = clientX - state.startX
    const dy = clientY - state.startY

    // Lock direction on first significant movement
    if (!state.directionLocked && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      state.directionLocked = true
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical — abort drag, let browser scroll
        dragRef.current = null
        return
      }
    }

    if (!state.directionLocked) return

    state.isDragging = true
    state.currentX = clientX

    const trackEl = sliderTrackRef.current
    if (trackEl && state.containerWidth > 0) {
      const isMobile = state.containerWidth < 640
      const monthPercent = monthsPerView === 'single' ? 100
        : monthsPerView === 'double' ? 50
          : isMobile ? 100 : 50
      const baseOffset = monthOffset * -monthPercent
      const dragPercent = (dx / state.containerWidth) * 100
      trackEl.style.transition = 'none'
      trackEl.style.transform = `translateX(${baseOffset + dragPercent}%)`
    }
  }, [monthOffset, monthsPerView])

  const dragEnd = useCallback(() => {
    const state = dragRef.current
    const trackEl = sliderTrackRef.current
    dragRef.current = null
    if (!state || !trackEl || !state.isDragging) return

    const dx = state.currentX - state.startX
    const threshold = state.containerWidth * 0.2

    // Re-enable CSS transition and clear inline transform so CSS var takes over
    trackEl.style.transition = ''
    trackEl.style.transform = ''

    // Suppress the click event that fires after mouseup/touchend
    didDragRef.current = true
    requestAnimationFrame(() => { didDragRef.current = false })

    if (dx < -threshold && monthOffset < maxMonthOffset) {
      setMonthOffset(prev => prev + 1)
    } else if (dx > threshold && monthOffset > 0) {
      setMonthOffset(prev => prev - 1)
    }
  }, [monthOffset, maxMonthOffset])

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStart(e.touches[0].clientX, e.touches[0].clientY)
  }, [dragStart])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    dragMove(e.touches[0].clientX, e.touches[0].clientY)
  }, [dragMove])

  const handleTouchEnd = useCallback(() => { dragEnd() }, [dragEnd])

  // Mouse handlers (desktop drag)
  //
  // #1547: `mouseup` on `document` is not enough. Release the button over
  // browser chrome, over another window, or after an alt-tab and the event
  // never arrives — `dragEnd` never runs, so `dragRef` stays set and
  // `handleDayHover`'s `if (dragRef.current && dateStr) return` swallows every
  // hover tip in the calendar until the widget remounts. Two more ways out:
  // the first move with no button held (`buttons === 0`), and window blur.
  // The listener pair also used to leak one set per drag, and survive unmount.
  const dragTeardownRef = useRef<(() => void) | null>(null)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragTeardownRef.current?.()   // never stack two drags
    dragStart(e.clientX, e.clientY)

    const teardown = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('blur', onWindowBlur)
      dragTeardownRef.current = null
    }
    const finish = () => { dragEnd(); teardown() }
    const onMouseMove = (ev: MouseEvent) => {
      if (ev.buttons === 0) { finish(); return }
      dragMove(ev.clientX, ev.clientY)
    }
    const onMouseUp = () => finish()
    const onWindowBlur = () => finish()

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    window.addEventListener('blur', onWindowBlur)
    dragTeardownRef.current = teardown
  }, [dragStart, dragMove, dragEnd])

  // A drag in flight must not outlive the component.
  useEffect(() => () => { dragTeardownRef.current?.() }, [])

  // Resolve tooltip message for a date
  // `tipCheckin`: the check-in this day was tried as a check-OUT for, or null
  // when the day was tried as a check-IN (see pendingTooltip).
  /**
   * Translate a rule rejection. CalendarUtils reports WHY through `reason` (and
   * carries a machine token in `message`); every user-facing string is resolved
   * here so a rejection can't leak untranslated copy onto a non-English feed —
   * the checkout branch below renders `message` verbatim for anything it does
   * not recognise.
   */
  const translateRuleMessage = useCallback((result: { message?: string; reason?: 'advance_notice' }, forDate: string): string | null => {
    if (result.reason === 'advance_notice') {
      return _t('error_advance_notice_error').replace('{{days}}', String(calendarUtils.getAdvanceNoticeDays()))
    }
    if (!result.message) return null
    if (result.message.includes('Minimum stay')) {
      const minStay = calendarUtils.getMinstay(forDate)
      return _t('error_check_in_min_stay_error').replace('{{value}}', minStayValueLabel(_t, minStay))
    }
    if (result.message.includes('Unavailable dates in range')) {
      return _t('error_select_range_between_unavailable_days_error')
    }
    return null
  }, [calendarUtils, _t])

  const getTooltipMessage = useCallback((dateStr: string, tipCheckin: string | null): string | null => {
    if (!calendarUtils.hasPriceData()) return null

    // Past and beyond-window days have no rule to explain and nothing to
    // suggest — a tip there is interactive noise on every leading greyed cell
    // of a 13-month grid, and each one used to trigger a fresh +/-30-day scan.
    if (calendarUtils.isOutsideWindow(dateStr)) return null

    const isUnavail = calendarUtils.isUnavailable(dateStr)
    if (isUnavail) {
      return _t('error_disabled_error')
    }

    // Advance-notice window (#1071): explain the rule instead of the generic
    // disabled copy — in read-only mode too.
    if (calendarUtils.isNoticeBlocked(dateStr)) {
      return _t('error_advance_notice_error').replace('{{days}}', String(calendarUtils.getAdvanceNoticeDays()))
    }

    const isTrapped = calendarUtils.isTrapped(dateStr)
    if (isTrapped) {
      return _t('error_disabled_error')
    }

    const opts = calendarUtils.dateOptions(dateStr)
    if (!opts) return _t('error_disabled_error')

    // When selecting dates
    if (options.allowSelectDates && tipCheckin) {
      // Trying to select checkout
      const result = calendarUtils.canCheckinout(tipCheckin, dateStr)
      if (!result.status && (result.message || result.reason)) {
        // Never `return result.message` — those are internal tokens.
        return translateRuleMessage(result, tipCheckin) ?? _t('error_disabled_error')
      }
      // A valid check-out target: no tip. Falling through to check-in
      // validation mislabelled checkout-only days as unavailable (codex closure).
      return null
    }

    // Check-in validation
    if (options.allowSelectDates) {
      const ciResult = calendarUtils.canCheckin(dateStr)
      if (!ciResult.status && (ciResult.message || ciResult.reason)) {
        return translateRuleMessage(ciResult, dateStr) ?? _t('error_disabled_error')
      }
    }

    // Read-only mode: show min stay info on available dates
    if (!options.allowSelectDates) {
      const ciBooked = calendarUtils._checkinBooked(dateStr)
      const coBooked = calendarUtils._checkoutBooked(dateStr)
      if (ciBooked || coBooked) {
        return _t('error_disabled_error')
      }
      if (opts.available === 0) {
        return _t('error_disabled_error')
      }
      if (opts.min_stay > 1 && calendarUtils._canCheckinFlag(dateStr)) {
        return _t('error_check_in_min_stay_error').replace('{{value}}', minStayValueLabel(_t, opts.min_stay))
      }
    }

    return null
  }, [calendarUtils, options.allowSelectDates, _t])

  // The check-in the calendar is currently picking a check-out FOR, or null.
  // Self-heal (codex fresh pass): a selected check-in that stopped being
  // valid — the advance-notice cutoff crossed while the guest was picking —
  // must not strand the calendar in checkout mode, so it reads as "no
  // check-in" and the next click/hover starts a fresh stay.
  const pickingCheckoutFor = useCallback((): string | null => {
    if (!selectedCheckin || selectedCheckout) return null
    return calendarUtils.canCheckin(selectedCheckin).status ? selectedCheckin : null
  }, [calendarUtils, selectedCheckin, selectedCheckout])

  // Nearest-valid-stay suggestion for a rejected day (#1071, toolkit
  // calendar-hover-tips parity): while picking a check-out, the valid
  // check-out closest to the day; otherwise the nearest day that can start
  // a stay, with its first valid check-out.
  // resolveCheckinSuggestion walks up to 30 days either side x 2 candidates,
  // and each canCheckin miss can walk maxIterations (425) days — so an
  // unmemoized call per pointerenter re-ran the same scan for every cell of a
  // blocked window. Keyed by the pair that determines the answer; cleared
  // whenever the availability data or the utils instance changes.
  const suggestionCacheRef = useRef(new Map<string, StaySuggestion | null>())
  useEffect(() => {
    suggestionCacheRef.current = new Map()
  }, [calendarUtils])

  const getSuggestion = useCallback((dateStr: string, tipCheckin: string | null): StaySuggestion | null => {
    if (!options.allowSelectDates) return null
    // A complete range is the guest's answer, not a prompt. `pickingCheckoutFor`
    // returns null once a checkout is set, so without this the hover fell into
    // the no-check-in branch and offered a one-tap button that replaced BOTH
    // confirmed dates — no confirmation, no undo.
    if (!tipCheckin && selectedCheckin && selectedCheckout) return null

    const key = `${dateStr}|${tipCheckin ?? ''}`
    const cached = suggestionCacheRef.current.get(key)
    if (cached !== undefined) return cached

    // While picking a checkout, ALWAYS suggest a checkout for the chosen
    // check-in — including on the check-in cell itself, where a check-in
    // suggestion would silently replace the guest's choice (codex r1).
    const resolved = tipCheckin
      ? resolveCheckoutSuggestion(calendarUtils, tipCheckin, dateStr)
      : resolveCheckinSuggestion(calendarUtils, dateStr)
    suggestionCacheRef.current.set(key, resolved)
    return resolved
  }, [calendarUtils, options.allowSelectDates, selectedCheckin, selectedCheckout])

  // Button label: "Select Aug 30 – Sep 2 · 3 nights" in the page locale.
  const suggestionLabel = useCallback((s: StaySuggestion): string => {
    const locale = detectedLocale ? detectedLocale.replace('_', '-') : undefined
    const nowYear = calendarUtils.getDateToday().year
    const nights = _t(s.nights === 1 ? 'tip_night_one' : 'tip_night_other').replace('{{n}}', String(s.nights))
    return _t('tip_select_stay')
      .replace('{{from}}', formatSuggestionDay(s.checkin, locale, nowYear))
      .replace('{{to}}', formatSuggestionDay(s.checkout, locale, nowYear))
      .replace('{{nights}}', nights)
  }, [calendarUtils, detectedLocale, _t])

  /**
   * The tip's own 4s life. #1548: it used to fire unconditionally, so a
   * keyboard user who tabbed onto the suggestion button had it unmounted from
   * under them 4s after the tip opened — `document.activeElement` fell back to
   * <body> and their place in the tab order was gone. A focused tip keeps
   * resetting its clock instead; blurring it starts the countdown again.
   */
  const armTooltipAutoHide = useCallback(() => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
    tooltipTimerRef.current = setTimeout(() => {
      if (tipHasFocus(tooltipElRef.current)) {
        armTooltipAutoHide()
        return
      }
      setTooltip(null)
    }, 4000)
  }, [])

  // Position and show tooltip near a day cell (like WP plugin's showTip)
  const handleShowTooltip = useCallback((dateStr: string, element: HTMLElement, explicitCheckin?: string | null) => {
    const tipCheckin = explicitCheckin === undefined ? pickingCheckoutFor() : explicitCheckin
    const message = getTooltipMessage(dateStr, tipCheckin)
    if (!message) {
      setTooltip(null)
      return
    }

    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
    cancelTooltipHide()

    const container = containerRef.current
    if (!container) return

    const cellRect = element.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // Position relative to the container, accounting for scroll
    const cellCenterX = cellRect.left + cellRect.width / 2 - containerRect.left
    const cellTopRelative = cellRect.top - containerRect.top + container.scrollTop
    const cellBottomRelative = cellTopRelative + cellRect.height

    const suggestion = getSuggestion(dateStr, tipCheckin)

    // A tip carrying a stay suggestion is both wider and roughly twice as tall
    // as a bare message, so both the placement threshold and the horizontal
    // clamp have to know which one we're about to render.
    const tipMaxWidth = suggestion ? 260 : 200
    const tipHeight = suggestion ? 110 : 60

    // Place above if enough room, otherwise below.
    const spaceAbove = cellRect.top - containerRect.top
    const placement = spaceAbove > tipHeight ? 'top' : 'bottom'
    const top = placement === 'top'
      ? cellTopRelative - 10  // 10px gap above cell top, tooltip bottom-anchored here
      : cellBottomRelative + 10  // 10px gap below cell bottom

    // Keep the tip inside the container. It is absolutely positioned and
    // shrink-to-fit, so a cell near the right edge left it only a sliver of
    // space and the label wrapped into a tall thin column (an 84px-wide tip for
    // a cross-year "Select Jan 5, 2027 – Jan 8, 2027 · 3 nights").
    const half = tipMaxWidth / 2
    const left = containerRect.width >= tipMaxWidth + 8
      ? Math.max(half + 4, Math.min(cellCenterX, containerRect.width - half - 4))
      : cellCenterX

    setTooltip({ message, top, left, placement, suggestion, dateStr })

    armTooltipAutoHide()
  }, [getTooltipMessage, getSuggestion, pickingCheckoutFor, cancelTooltipHide, armTooltipAutoHide])

  // Hide the tip shortly after the pointer leaves a day cell; entering the
  // tip itself (to press the action button) cancels this.
  //
  // ONE deadline, measured from the first leave, rather than a fresh 350ms on
  // every arming call. This used to `cancelTooltipHide()` first, so each cell
  // boundary the pointer crossed restarted the clock and a sweep could hold a
  // stale tip up for as long as it lasted — in Chrome, three crossings logged
  // ARM#534 CLEAR#534 ARM#536 CLEAR#536 ARM#542.
  const scheduleTooltipHide = useCallback(() => {
    if (tooltipHideTimerRef.current) return
    tooltipHideTimerRef.current = setTimeout(() => {
      tooltipHideTimerRef.current = null
      setTooltip(null)
    }, HIDE_GRACE_MS)
  }, [])

  /**
   * Pointer resting on the tip's button: cancel the leave-hide grace so it stays
   * clickable, but RE-ARM the 4s auto-hide. Cancelling both (and re-arming
   * neither) pinned the tip up indefinitely — and because it renders over the
   * row above the hovered cell, those dates could not be hovered or clicked
   * until the pointer left the tip entirely.
   */
  const holdTooltipOpen = useCallback(() => {
    cancelTooltipHide()
    armTooltipAutoHide()
  }, [cancelTooltipHide, armTooltipAutoHide])

  // One-tap apply: exactly what two valid clicks on those days would do.
  // Re-validate at click time — the stored suggestion can go stale if the
  // availability data refreshed while the tooltip was open (codex r1).
  const handleApplySuggestion = useCallback((s: StaySuggestion) => {
    // The tip is about to unmount. If the guest got to its button by keyboard,
    // focus would land on <body> and their place in the document would be gone
    // (#1548) — hand it to the check-out day the suggestion just selected.
    const returnFocus = tipHasFocus(tooltipElRef.current)
    setHoveredDate(null)
    // Drop the pending hide with the tip, so a grace armed on the way to the
    // button cannot fire against the selection this apply just replaced.
    cancelTooltipHide()
    setTooltip(null)
    setPendingTooltip(null)
    hoverQuietUntilRef.current = Date.now() + 700
    // The playground live-updates options; a tooltip minted in select mode
    // must not apply after a flip to read-only (codex r2).
    if (!options.allowSelectDates) return
    if (!calendarUtils.canCheckinout(s.checkin, s.checkout).status) return
    setSelectedCheckin(s.checkin)
    setSelectedCheckout(s.checkout)
    // Bring the suggestion into view. resolveCheckinSuggestion searches +/-30
    // days, so on a long blocked stretch it routinely lands in the next month —
    // and applying it without navigating left the visible grid unchanged, i.e.
    // a button that reads as dead. The incoming `calendar-set-dates` handler
    // navigates for exactly this reason.
    const dt = DateTime.fromISO(s.checkin)
    const idx = allMonths.findIndex((m) => m.year === dt.year && m.month === dt.month)
    if (idx >= 0) setMonthOffset(Math.min(idx, maxMonthOffset))
    if (returnFocus) {
      setFocusedDate(s.checkout)
      pendingFocusRef.current = s.checkout
    }
  }, [calendarUtils, options.allowSelectDates, allMonths, maxMonthOffset])

  // Date selection logic — shows tooltip only on validation failure
  const handleDayClick = useCallback((dateStr: string, element: HTMLElement) => {
    // Ignore clicks that are the tail-end of a drag gesture
    if (didDragRef.current) return

    // A click resolves whatever the hover was proposing: drop any pending hide
    // so it cannot fire against the state this click is about to change.
    cancelTooltipHide()

    if (!options.allowSelectDates) {
      // Read-only mode: just show tooltip
      handleShowTooltip(dateStr, element)
      return
    }

    const checkoutFor = pickingCheckoutFor()
    const isSelectStart = !checkoutFor

    if (isSelectStart) {
      // Selecting check-in
      const result = calendarUtils.canCheckin(dateStr)
      if (!result.status) {
        handleShowTooltip(dateStr, element)
        return
      }

      setTooltip(null)
      setPendingTooltip(null)
      setSelectedCheckin(dateStr)
      setSelectedCheckout(null)
      setHoveredDate(null)
    } else {
      // Selecting check-out
      const result = calendarUtils.canCheckinout(checkoutFor, dateStr)
      if (!result.status) {
        handleShowTooltip(dateStr, element, checkoutFor)
        return
      }

      setTooltip(null)
      setPendingTooltip(null)
      setSelectedCheckout(dateStr)
      setHoveredDate(null)
    }
  }, [options.allowSelectDates, calendarUtils, handleShowTooltip, pickingCheckoutFor])

  const handleDayHover = useCallback((dateStr: string | null, element?: HTMLElement) => {
    if (!options.allowSelectDates) return
    // A pressed pointer means a month drag may be in flight — sweeping the
    // grid must not pop tooltips on every cell crossed (codex r1). Leaves
    // still pass so the starting cell's tip/preview clears (codex fresh pass).
    if (dragRef.current && dateStr) return

    // #1531: a cell the open suggestion tip is sitting on top of is not a hover
    // target — the guest cannot see it, and it is only under the pointer because
    // the tip's chrome is `pointer-events: none` and they are on their way to
    // its button. Hold the tip exactly as it is and keep any pending hide
    // cancelled: the pointer is still on the tip, which is the thing being
    // interacted with. Cells the tip does not cover are ordinary hovers.
    const openTip = tooltipRef.current
    if (dateStr && element && openTip?.suggestion && isUnderTip(tooltipElRef.current, element)) {
      // The range preview keeps following the pointer; only the tip is held.
      if (pickingCheckoutFor()) setHoveredDate(dateStr)
      cancelTooltipHide()
      return
    }

    const checkoutFor = pickingCheckoutFor()
    if (checkoutFor) {
      setHoveredDate(dateStr)
      // While picking checkout, hovering an invalid date shows its tooltip
      // (min-stay copy included) like the WP plugin; valid targets clear it (#603).
      if (dateStr && dateStr !== checkoutFor && element) {
        if (Date.now() < hoverQuietUntilRef.current) return
        handleShowTooltip(dateStr, element, checkoutFor)
      } else if (!dateStr) {
        scheduleTooltipHide()
      }
      return
    }

    // No check-in yet (or a full range already picked, where the next click
    // starts over): hovering a day that can't START a stay shows the same
    // reason its click would (#1071, toolkit calendar-hover-tips parity).
    if (dateStr && element) {
      if (Date.now() < hoverQuietUntilRef.current) return
      handleShowTooltip(dateStr, element, null)
    } else if (!dateStr) {
      scheduleTooltipHide()
    }
  }, [options.allowSelectDates, handleShowTooltip, scheduleTooltipHide, cancelTooltipHide, pickingCheckoutFor])

  // Show queued tooltip after slider transition completes (0.4s CSS transition)
  useEffect(() => {
    if (!pendingTooltip) return
    const timer = setTimeout(() => {
      const container = containerRef.current
      if (!container) return
      const cell = container.querySelector(`[data-date="${pendingTooltip.date}"]`) as HTMLElement
      if (cell) {
        handleShowTooltip(pendingTooltip.date, cell, pendingTooltip.checkin)
      }
      setPendingTooltip(null)
    }, 450)
    return () => clearTimeout(timer)
  }, [pendingTooltip, handleShowTooltip])

  const handleClear = useCallback(() => {
    setSelectedCheckin(null)
    setSelectedCheckout(null)
    setHoveredDate(null)
    cancelTooltipHide()
    setTooltip(null)
    setPendingTooltip(null)
  }, [cancelTooltipHide])

  // ---------------------------------------------------------------------------
  // #1548: keyboard access to the day grid.
  //
  // Day cells were bare <div>s with an onClick — no tabIndex, no role, no key
  // handling — so dates could not be selected without a mouse and the tips
  // explaining WHY a day was rejected (minimum stay, advance notice) were
  // unreachable. One roving tabstop moves with the arrow keys, as in the
  // WAI-ARIA grid pattern: the grid is a single tabstop, and Tab from it lands
  // on the suggestion button when a tip is open.
  // ---------------------------------------------------------------------------

  /** First day the grid will accept as a tabstop: the check-in, else today, else the first day shown. */
  const defaultFocusDate = useMemo(() => {
    if (!allMonths.length) return null
    if (selectedCheckin) return selectedCheckin
    const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd')
    const first = allMonths[0]
    const firstOfView = DateTime.fromObject({ year: first.year, month: first.month, day: 1 }).toFormat('yyyy-MM-dd')
    return today >= firstOfView ? today : firstOfView
  }, [allMonths, selectedCheckin, timezone])

  useEffect(() => {
    setFocusedDate((prev) => prev ?? defaultFocusDate)
  }, [defaultFocusDate])

  /** Scroll the slider so `dateStr`'s month is on screen, without moving the page. */
  const revealMonthFor = useCallback((dateStr: string) => {
    const dt = DateTime.fromISO(dateStr)
    const idx = allMonths.findIndex((m) => m.year === dt.year && m.month === dt.month)
    if (idx < 0) return
    const perView = monthsPerView === 'single' ? 1 : 2
    setMonthOffset((prev) => {
      if (idx < prev) return Math.min(idx, maxMonthOffset)
      if (idx > prev + perView - 1) return Math.min(idx - perView + 1, maxMonthOffset)
      return prev
    })
  }, [allMonths, monthsPerView, maxMonthOffset])

  const handleDayKeyDown = useCallback((dateStr: string, e: React.KeyboardEvent<HTMLElement>) => {
    const step = (days: number) => {
      const next = DateTime.fromISO(dateStr).plus({ days }).toFormat('yyyy-MM-dd')
      const dt = DateTime.fromISO(next)
      // Don't walk off either end of the rendered range.
      if (!allMonths.some((m) => m.year === dt.year && m.month === dt.month)) return
      e.preventDefault()
      setFocusedDate(next)
      pendingFocusRef.current = next
      revealMonthFor(next)
    }

    switch (e.key) {
      case 'ArrowLeft': step(-1); break
      case 'ArrowRight': step(1); break
      case 'ArrowUp': step(-7); break
      case 'ArrowDown': step(7); break
      case 'Home': step(-((DateTime.fromISO(dateStr).weekday % 7) - firstDayOfWeek + 7) % 7); break
      case 'End': step(6 - (((DateTime.fromISO(dateStr).weekday % 7) - firstDayOfWeek + 7) % 7)); break
      case 'PageUp': step(-DateTime.fromISO(dateStr).daysInMonth!); break
      case 'PageDown': step(DateTime.fromISO(dateStr).plus({ months: 1 }).daysInMonth!); break
      case 'Enter':
      case ' ':
        e.preventDefault()
        handleDayClick(dateStr, e.currentTarget as HTMLElement)
        break
      case 'Escape':
        if (tooltipRef.current) { e.preventDefault(); cancelTooltipHide(); setTooltip(null) }
        break
      default: break
    }
  }, [allMonths, firstDayOfWeek, revealMonthFor, handleDayClick, cancelTooltipHide])

  // Pull DOM focus to the cell the arrow keys moved to. `preventScroll` matters:
  // the months strip is an overflow-hidden transform slider, and letting the
  // browser scroll a just-revealed month into view drags the whole strip sideways.
  useEffect(() => {
    const want = pendingFocusRef.current
    if (!want) return
    const el = containerRef.current?.querySelector(`[data-date="${want}"]`) as HTMLElement | null
    if (!el) return
    pendingFocusRef.current = null
    el.focus({ preventScroll: true })
  })

  /** Focus reaching a day is the keyboard's equivalent of hovering it. */
  const handleDayFocus = useCallback((dateStr: string, element: HTMLElement) => {
    setFocusedDate(dateStr)
    if (!options.allowSelectDates) return
    if (Date.now() < hoverQuietUntilRef.current) return
    handleShowTooltip(dateStr, element)
  }, [options.allowSelectDates, handleShowTooltip])

  if (isLoadingAvailability || isLoadingProperty) {
    // Availability is a client-side fetch (#1229), so this skeleton is what the
    // server renders and what the client hydrates against — it holds the real
    // grid's space instead of collapsing and shifting the page when the data
    // lands. The flags come from data that IS available on both sides
    // (`property` is SSR-prefetched, the rest are settings), so the server and
    // the client's first render agree.
    return (
      <SkeletonComponent
        monthsPerView={monthsPerView}
        reserveHeader={!!discountText}
        reserveFooter={options.showFooterInfo !== false}
        reserveLocalTime={!!options.showLocalTime}
      />
    )
  }

  if (isErrorAvailability || isErrorProperty) {
    return <WidgetDataError error={errorAvailability ?? errorProperty} />
  }

  if (!availability) {
    return null
  }

  return (
    <div className="custom-calendar-container homelocal-availability-calendar-container" ref={containerRef}>
      {/* Header: discount offer (matches WP plugin). Local time moved to the
          footer with the legend lines (#997) — it read as floating between the
          promo tag and the grid up here. */}
      {discountText && (
        <div className="calendar-header-info">
          <div className="discount-text-container">
            <div className="discount-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="currentColor" opacity="0" />
                <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" fill="currentColor" />
              </svg>
            </div>
            <span className="discount-text">{discountText}</span>
          </div>
        </div>
      )}

      {/* Calendar body: navigation buttons + months slider */}
      <div className="calendar-body">
        <button
          className="nav-button prev-btn"
          onClick={handlePrev}
          disabled={monthOffset <= 0}
          aria-label="Previous month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          className="nav-button next-btn"
          onClick={handleNext}
          disabled={monthOffset >= maxMonthOffset}
          aria-label="Next month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div
          className={`months-container${monthsPerView !== 'auto' ? ` months-per-view-${monthsPerView}` : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div
            ref={sliderTrackRef}
            className="slider-track"
            style={{ '--month-offset': monthOffset } as React.CSSProperties}
          >
            {allMonths.map(({ year, month }) => (
              <MonthComponent
                key={`${year}-${month}`}
                year={year}
                month={month}
                calendarUtils={calendarUtils}
                availability={availability}
                showPrice={options.showPrice}
                showBookedPrice={options.showBookedPrice}
                /* Feed currency first (#994): the booking widget on the same
                   page resolves property.currency_code, and the stored
                   setting is an indistinguishable-from-default 'USD'. */
                currency={property?.currency_code || options.currency}
                locale={priceLocale}
                weekdayLabels={weekdayLabels}
                firstDayOfWeek={firstDayOfWeek}
                monthLabel={getMonthLabel(year, month, detectedLocale)}
                allowSelectDates={options.allowSelectDates}
                selectedCheckin={selectedCheckin}
                selectedCheckout={selectedCheckout}
                hoveredDate={hoveredDate}
                onDayClick={handleDayClick}
                onDayHover={handleDayHover}
                focusedDate={focusedDate}
                onDayKeyDown={handleDayKeyDown}
                onDayFocus={handleDayFocus}
                tooltipDate={tooltip?.dateStr ?? null}
                tooltipId={tipDomId}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip: top placement anchors bottom edge to cell top, bottom placement anchors top edge to cell bottom */}
      {tooltip && (
        <div
          ref={tooltipElRef}
          id={tipDomId}
          role="tooltip"
          className={`homelocal-tip${tooltip.suggestion ? ' tip-actionable' : ''}`}
          data-placement={tooltip.placement}
          style={{
            display: 'block',
            position: 'absolute',
            top: tooltip.top,
            left: tooltip.left,
            // Explicit width for the actionable tip. It is absolutely positioned
            // with `left` and no `right`, so shrink-to-fit is capped by the space
            // remaining to the container's right edge — near the last column that
            // squeezed a full date range into an ~84px column. `left` is already
            // clamped so this width always lands inside the container.
            ...(tooltip.suggestion ? { width: 260, maxWidth: 'none' as const } : {}),
            transform: tooltip.placement === 'top'
              ? 'translate(-50%, -100%)'
              : 'translateX(-50%)',
            zIndex: 9999990,
          }}
          /* Keep the tip alive while the pointer is on its action button, and
             give leaving it the same grace a day cell gets. These fire for the
             BUTTON, not the whole tip: the rest of the tip is
             `pointer-events: none`, so the pointer "inside" the tip is really
             over the day cell behind it. Hiding outright here meant nudging a
             few pixels off the button — onto the tip's own message — killed the
             tip in ~30ms with no grace at all, which is the bug #1531 was filed
             for. With a grace armed instead, the covered cell's hover is caught
             by the travel guard and the tip survives. Touch pointers are
             skipped: on tap the tip must keep its 4s window. */
          onPointerEnter={(e) => {
            if (e.pointerType === 'touch') return
            holdTooltipOpen()
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'touch') return
            scheduleTooltipHide()
          }}
          /* #1548: Tab from the focused day lands on the action button, which
             lives inside this tip. Keep it alive while it holds focus, and give
             leaving it by keyboard the same grace as leaving it by pointer. */
          onFocusCapture={cancelTooltipHide}
          onBlurCapture={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            scheduleTooltipHide()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return
            e.preventDefault()
            cancelTooltipHide()
            setTooltip(null)
            // Send focus back to the day the tip belongs to, not to <body>.
            const cell = containerRef.current?.querySelector(`[data-date="${tooltip.dateStr}"]`) as HTMLElement | null
            cell?.focus({ preventScroll: true })
          }}
        >
          <div className="tip-content">
            {tooltip.message}
            {tooltip.suggestion && (
              <button
                type="button"
                className="tip-apply-btn"
                onClick={() => handleApplySuggestion(tooltip.suggestion!)}
              >
                {suggestionLabel(tooltip.suggestion)}
              </button>
            )}
          </div>
          <div className="tip-arrow" />
        </div>
      )}

      {/* Footer: instructions bar + color legend (matches WP plugin calendar.php).
          A widget that injects FooterComponent (#920) owns this block entirely. */}
      {options.showFooterInfo && FooterComponent && (
        <FooterComponent
          onClear={handleClear}
          canClear={Boolean(selectedCheckin || selectedCheckout)}
          _t={_t}
          sampleDay={calendarUtils.getDateToday().day}
        />
      )}
      {options.showFooterInfo && !FooterComponent && (
        <div className="calendar-footer-info">
          {/* Instructions bar: taxes note (left) | swipe icon (center) | clear dates (right) */}
          <div className="footer-instructions">
            <div className="footer-taxes-note">{_t('footer_taxes_note')}</div>
            {options.showClearButton && options.allowSelectDates && (
              <button className="footer-clear-btn" onClick={handleClear} disabled={!selectedCheckin && !selectedCheckout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="9" y1="14" x2="15" y2="20" />
                  <line x1="15" y1="14" x2="9" y2="20" />
                </svg>
                {_t('footer_clear_dates')}
              </button>
            )}
          </div>

          {/* Color legend */}
          <div className="footer-legend">
            <div className="footer-legend-item">
              <span className="footer-legend-swatch footer-legend-swatch--available" />
              <span>{_t('footer_legend_available')}</span>
            </div>
            <div className="footer-legend-item">
              <span className="footer-legend-swatch footer-legend-swatch--booked" />
              <span>{_t('footer_legend_booked')}</span>
            </div>
            <div className="footer-legend-item">
              <span className="footer-legend-swatch footer-legend-swatch--unavailable" />
              <span>{_t('footer_legend_unavailable')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Local time in the property's timezone — lives with the footer's muted
          lines (#997). Label and value are separate spans with a flex gap, so
          spacing never depends on a trailing space in the translation string. */}
      {options.showLocalTime && localTime && (
        <div className="local-time">
          <span className="local-time-label">{_t('header_local_time')}</span>
          <span className="local-time-datetime">{localTime}</span>
        </div>
      )}
    </div>
  )
}
